import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { TeacherCorrectionToolbar } from "./TeacherCorrectionToolbar";
import {
  estimateTextBounds,
  exportSlopFromCanvas,
  hitTestAnnotations,
  normalizeRect,
  resizeRectangle,
  type RectCorner,
} from "./teacherCorrectionGeometry";
import type {
  PenStroke,
  RectangleShape,
  TeacherAnnotation,
  TeacherCorrectionPayload,
  TeacherCorrectionTool,
  TextAnnotation,
} from "./teacherCorrectionTypes";
import { createAnnotationId } from "./teacherCorrectionTypes";

const MAX_EXPORT_EDGE = 2048;
const DEFAULT_COLOR = "#dc2626";
const BASE_STROKE = 4;
const MIN_RECT_SIZE = 8;
const RECT_COMMIT_MIN = 4;

/** CSS transform scale on the image stack (canvas coords stay in export space). */
const USER_ZOOM_MIN = 0.5;
const USER_ZOOM_MAX = 3;
const USER_ZOOM_STEP = 0.1;

function clampUserZoom(z: number): number {
  const n = Math.round(z * 100) / 100;
  return Math.min(USER_ZOOM_MAX, Math.max(USER_ZOOM_MIN, n));
}

function computeExportSize(naturalW: number, naturalH: number): { w: number; h: number } {
  if (naturalW <= 0 || naturalH <= 0) return { w: 1, h: 1 };
  const scale = Math.min(1, MAX_EXPORT_EDGE / Math.max(naturalW, naturalH));
  return {
    w: Math.max(1, Math.round(naturalW * scale)),
    h: Math.max(1, Math.round(naturalH * scale)),
  };
}

function strokeWidthForExport(exportW: number): number {
  return Math.max(2, Math.round((BASE_STROKE * exportW) / 800));
}

function fontSizeForExport(exportW: number): number {
  return Math.max(14, Math.round((16 * exportW) / 800));
}

function drawAnnotations(
  ctx: CanvasRenderingContext2D,
  list: TeacherAnnotation[],
  exportW: number,
  skipTextId?: string | null
) {
  const sw = strokeWidthForExport(exportW);
  for (const a of list) {
    if (a.tool === "pen") {
      const lw = a.strokeWidth ?? sw;
      ctx.strokeStyle = a.color;
      ctx.lineWidth = lw;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      if (a.points.length === 0) continue;
      if (a.points.length === 1) {
        const p = a.points[0];
        ctx.beginPath();
        ctx.arc(p.x, p.y, lw / 2, 0, Math.PI * 2);
        ctx.fillStyle = a.color;
        ctx.fill();
        continue;
      }
      ctx.beginPath();
      ctx.moveTo(a.points[0].x, a.points[0].y);
      for (let i = 1; i < a.points.length; i++) {
        ctx.lineTo(a.points[i].x, a.points[i].y);
      }
      ctx.stroke();
    } else if (a.tool === "rectangle") {
      const lw = a.strokeWidth ?? sw;
      ctx.strokeStyle = a.color;
      ctx.lineWidth = lw;
      ctx.strokeRect(a.x, a.y, a.width, a.height);
    } else if (a.tool === "text") {
      if (skipTextId && a.id === skipTextId) continue;
      drawTextAnnotation(ctx, a, exportW);
    }
  }
}

function drawTextAnnotation(ctx: CanvasRenderingContext2D, t: TextAnnotation, exportW: number) {
  const fs = t.fontSize ?? fontSizeForExport(exportW);
  const lines = (t.text || "").split("\n");
  if (lines.length === 1 && lines[0] === "") return;
  ctx.save();
  ctx.fillStyle = t.color;
  ctx.font = `${fs}px sans-serif`;
  ctx.textBaseline = "top";
  const lh = fs * 1.35;
  lines.forEach((line, i) => {
    ctx.fillText(line, t.x, t.y + i * lh);
  });
  ctx.restore();
}

function drawSelectionChrome(
  ctx: CanvasRenderingContext2D,
  annotations: TeacherAnnotation[],
  selectedId: string | null,
  exportW: number
) {
  if (!selectedId) return;
  const ann = annotations.find((a) => a.id === selectedId);
  if (!ann) return;
  ctx.save();
  ctx.setLineDash([6, 4]);
  ctx.strokeStyle = "rgba(59, 130, 246, 0.9)";
  ctx.lineWidth = Math.max(1, strokeWidthForExport(exportW) / 2);
  if (ann.tool === "rectangle") {
    ctx.strokeRect(ann.x, ann.y, ann.width, ann.height);
    const h = strokeWidthForExport(exportW) * 2;
    const corners = [
      [ann.x, ann.y],
      [ann.x + ann.width, ann.y],
      [ann.x, ann.y + ann.height],
      [ann.x + ann.width, ann.y + ann.height],
    ] as const;
    ctx.setLineDash([]);
    ctx.fillStyle = "#fff";
    ctx.strokeStyle = "#2563eb";
    for (const [cx, cy] of corners) {
      ctx.fillRect(cx - h / 2, cy - h / 2, h, h);
      ctx.strokeRect(cx - h / 2, cy - h / 2, h, h);
    }
  } else if (ann.tool === "text") {
    const b = estimateTextBounds(ann);
    ctx.strokeRect(b.x, b.y, b.w, b.h);
  }
  ctx.restore();
}

type RectDraft = { x0: number; y0: number; x1: number; y1: number };

type Gesture =
  | { type: "none" }
  | { type: "pen" }
  | { type: "rect-create" }
  | {
      type: "move";
      id: string;
      start: { x: number; y: number };
      snapshot: TeacherAnnotation;
    }
  | {
      type: "resize";
      id: string;
      handle: RectCorner;
      initial: RectangleShape;
    };

export type TeacherImageCorrectionEditorRef = {
  hasAnnotations: () => boolean;
  getAnnotationPayload: () => TeacherCorrectionPayload | null;
  exportMergedImage: (filename: string) => Promise<File>;
};

export interface TeacherImageCorrectionEditorProps {
  imageUrl: string;
  submissionId: string;
  onImageReadyChange: (ready: boolean) => void;
}

export const TeacherImageCorrectionEditor = forwardRef<
  TeacherImageCorrectionEditorRef,
  TeacherImageCorrectionEditorProps
>(({ imageUrl, submissionId, onImageReadyChange }, ref) => {
  const [loadStatus, setLoadStatus] = useState<"idle" | "loading" | "ready" | "error">("loading");
  const [exportSize, setExportSize] = useState({ w: 0, h: 0 });
  const [annotations, setAnnotations] = useState<TeacherAnnotation[]>([]);
  const [activeTool, setActiveTool] = useState<TeacherCorrectionTool>("pen");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editingTextId, setEditingTextId] = useState<string | null>(null);
  const [rectDraft, setRectDraft] = useState<RectDraft | null>(null);
  const rectDraftRef = useRef<RectDraft | null>(null);
  useEffect(() => {
    rectDraftRef.current = rectDraft;
  }, [rectDraft]);
  const [displayScale, setDisplayScale] = useState(1);
  /** View zoom (1 = 100%); image + canvas scaled together so marks stay aligned. */
  const [userZoom, setUserZoom] = useState(1);

  const zoomWheelRef = useRef<HTMLDivElement>(null);

  const activeToolRef = useRef<TeacherCorrectionTool>("pen");
  useEffect(() => {
    activeToolRef.current = activeTool;
  }, [activeTool]);

  const editingTextIdRef = useRef<string | null>(null);
  useEffect(() => {
    editingTextIdRef.current = editingTextId;
  }, [editingTextId]);

  const displayImgRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const annotationsRef = useRef<TeacherAnnotation[]>([]);
  useEffect(() => {
    annotationsRef.current = annotations;
  }, [annotations]);

  const selectedIdRef = useRef<string | null>(null);
  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);

  const penDraftRef = useRef<{ x: number; y: number }[]>([]);
  const isPenDownRef = useRef(false);
  const gestureRef = useRef<Gesture>({ type: "none" });

  const updateDisplayScale = useCallback(() => {
    const img = displayImgRef.current;
    const canvas = canvasRef.current;
    if (!img || !canvas || exportSize.w <= 0) return;
    const w = img.clientWidth;
    if (w > 0) setDisplayScale(w / exportSize.w);
  }, [exportSize.w]);

  const endTextEditing = useCallback(() => {
    const id = editingTextIdRef.current;
    if (!id) return;
    const t = annotationsRef.current.find(
      (a): a is TextAnnotation => a.id === id && a.tool === "text"
    );
    const removed = !t || t.text.trim().length === 0;
    setAnnotations((prev) =>
      prev.filter((a) => {
        if (a.id !== id) return true;
        if (a.tool !== "text") return true;
        return a.text.trim().length > 0;
      })
    );
    setEditingTextId(null);
    if (removed) setSelectedId((sid) => (sid === id ? null : sid));
  }, []);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || exportSize.w <= 0 || exportSize.h <= 0) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    drawAnnotations(ctx, annotationsRef.current, exportSize.w, editingTextIdRef.current);
    drawSelectionChrome(ctx, annotationsRef.current, selectedIdRef.current, exportSize.w);

    const sw = strokeWidthForExport(exportSize.w);
    const draft = penDraftRef.current;
    if (draft.length > 0) {
      ctx.strokeStyle = DEFAULT_COLOR;
      ctx.lineWidth = sw;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      if (draft.length === 1) {
        ctx.beginPath();
        ctx.arc(draft[0].x, draft[0].y, sw / 2, 0, Math.PI * 2);
        ctx.fillStyle = DEFAULT_COLOR;
        ctx.fill();
      } else {
        ctx.beginPath();
        ctx.moveTo(draft[0].x, draft[0].y);
        for (let i = 1; i < draft.length; i++) {
          ctx.lineTo(draft[i].x, draft[i].y);
        }
        ctx.stroke();
      }
    }

    if (rectDraft) {
      const { x, y, w, h } = normalizeRect(rectDraft.x0, rectDraft.y0, rectDraft.x1, rectDraft.y1);
      ctx.strokeStyle = DEFAULT_COLOR;
      ctx.lineWidth = sw;
      ctx.setLineDash([4, 4]);
      ctx.strokeRect(x, y, w, h);
      ctx.setLineDash([]);
    }
  }, [exportSize.w, exportSize.h, rectDraft]);

  useEffect(() => {
    redraw();
  }, [annotations, exportSize, rectDraft, selectedId, editingTextId, redraw]);

  useEffect(() => {
    onImageReadyChange(false);
    setLoadStatus("loading");
    setAnnotations([]);
    setSelectedId(null);
    setEditingTextId(null);
    setRectDraft(null);
    penDraftRef.current = [];
    gestureRef.current = { type: "none" };
    setExportSize({ w: 0, h: 0 });
    setUserZoom(1);
  }, [imageUrl, submissionId, onImageReadyChange]);

  const handleZoomIn = useCallback(() => {
    setUserZoom((z) => clampUserZoom(z + USER_ZOOM_STEP));
  }, []);

  const handleZoomOut = useCallback(() => {
    setUserZoom((z) => clampUserZoom(z - USER_ZOOM_STEP));
  }, []);

  const handleResetZoom = useCallback(() => {
    setUserZoom(1);
  }, []);

  useEffect(() => {
    const el = zoomWheelRef.current;
    if (!el || loadStatus !== "ready") return;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      const delta = e.deltaY > 0 ? -USER_ZOOM_STEP : USER_ZOOM_STEP;
      setUserZoom((z) => clampUserZoom(z + delta));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [loadStatus]);

  const handleImgLoad = useCallback(() => {
    const el = displayImgRef.current;
    if (!el || el.naturalWidth <= 0) return;
    const { w, h } = computeExportSize(el.naturalWidth, el.naturalHeight);
    setExportSize({ w, h });
    setLoadStatus("ready");
    onImageReadyChange(true);
    requestAnimationFrame(() => {
      const img = displayImgRef.current;
      if (img && w > 0) setDisplayScale(img.clientWidth / w);
    });
  }, [onImageReadyChange]);

  const handleImgError = useCallback(() => {
    setLoadStatus("error");
    onImageReadyChange(false);
  }, [onImageReadyChange]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || loadStatus !== "ready" || exportSize.w <= 0) return;
    canvas.width = exportSize.w;
    canvas.height = exportSize.h;
    redraw();
  }, [loadStatus, exportSize, redraw]);

  useEffect(() => {
    if (loadStatus !== "ready") return;
    const ro = new ResizeObserver(() => updateDisplayScale());
    const img = displayImgRef.current;
    if (img) ro.observe(img);
    window.addEventListener("resize", updateDisplayScale);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", updateDisplayScale);
    };
  }, [loadStatus, updateDisplayScale]);

  useEffect(() => {
    if (!editingTextId) return;
    const t = window.setTimeout(() => textareaRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [editingTextId]);

  const getPoint = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>): { x: number; y: number } | null => {
      const canvas = canvasRef.current;
      if (!canvas) return null;
      const rect = canvas.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return null;
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      return {
        x: (e.clientX - rect.left) * scaleX,
        y: (e.clientY - rect.top) * scaleY,
      };
    },
    []
  );

  const endPen = useCallback(() => {
    if (!isPenDownRef.current) return;
    isPenDownRef.current = false;
    const pts = [...penDraftRef.current];
    penDraftRef.current = [];
    gestureRef.current = { type: "none" };
    if (pts.length === 0) {
      redraw();
      return;
    }
    const sw = strokeWidthForExport(exportSize.w);
    const stroke: PenStroke = {
      id: createAnnotationId(),
      tool: "pen",
      points: pts,
      color: DEFAULT_COLOR,
      strokeWidth: sw,
    };
    setAnnotations((prev) => [...prev, stroke]);
  }, [exportSize.w, redraw]);

  const commitRectDraft = useCallback(() => {
    const g = gestureRef.current;
    if (g.type !== "rect-create") return;
    const draft = rectDraftRef.current;
    gestureRef.current = { type: "none" };
    setRectDraft(null);
    if (!draft) {
      redraw();
      return;
    }
    const { x, y, w, h } = normalizeRect(draft.x0, draft.y0, draft.x1, draft.y1);
    if (w < RECT_COMMIT_MIN || h < RECT_COMMIT_MIN) {
      redraw();
      return;
    }
    const sw = strokeWidthForExport(exportSize.w);
    const shape: RectangleShape = {
      id: createAnnotationId(),
      tool: "rectangle",
      x,
      y,
      width: Math.max(MIN_RECT_SIZE, w),
      height: Math.max(MIN_RECT_SIZE, h),
      color: DEFAULT_COLOR,
      strokeWidth: sw,
    };
    setAnnotations((prev) => [...prev, shape]);
    setSelectedId(shape.id);
  }, [exportSize.w, redraw]);

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (loadStatus !== "ready") return;
    e.preventDefault();
    (e.target as HTMLCanvasElement).setPointerCapture(e.pointerId);

    if (editingTextIdRef.current) {
      endTextEditing();
    }

    const pt = getPoint(e);
    if (!pt) return;

    const tool = activeToolRef.current;
    const canvas = canvasRef.current;
    const slop = canvas ? exportSlopFromCanvas(canvas.width, canvas.getBoundingClientRect().width) : 12;

    if (tool === "pen") {
      isPenDownRef.current = true;
      gestureRef.current = { type: "pen" };
      penDraftRef.current = [pt];
      redraw();
      return;
    }

    if (tool === "rectangle") {
      gestureRef.current = { type: "rect-create" };
      setRectDraft({ x0: pt.x, y0: pt.y, x1: pt.x, y1: pt.y });
      setSelectedId(null);
      redraw();
      return;
    }

    if (tool === "text") {
      const fs = fontSizeForExport(exportSize.w);
      const id = createAnnotationId();
      const ann: TextAnnotation = {
        id,
        tool: "text",
        x: pt.x,
        y: pt.y,
        text: "",
        color: DEFAULT_COLOR,
        fontSize: fs,
      };
      setAnnotations((prev) => [...prev, ann]);
      setSelectedId(id);
      setEditingTextId(id);
      gestureRef.current = { type: "none" };
      return;
    }

    if (tool === "select") {
      const hit = hitTestAnnotations(pt.x, pt.y, annotationsRef.current, selectedIdRef.current, slop);
      if (!hit) {
        setSelectedId(null);
        gestureRef.current = { type: "none" };
        redraw();
        return;
      }
      setSelectedId(hit.id);
      const target = annotationsRef.current.find((a) => a.id === hit.id);
      if (!target) return;

      if (hit.hit === "handle" && target.tool === "rectangle" && hit.handle) {
        gestureRef.current = {
          type: "resize",
          id: hit.id,
          handle: hit.handle,
          initial: { ...target },
        };
        return;
      }

      if (hit.hit === "body") {
        if (target.tool === "rectangle" || target.tool === "text") {
          gestureRef.current = {
            type: "move",
            id: hit.id,
            start: { x: pt.x, y: pt.y },
            snapshot: { ...target },
          };
        }
      }
    }
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (loadStatus !== "ready") return;
    const pt = getPoint(e);
    if (!pt) return;

    const g = gestureRef.current;

    if (g.type === "pen" && isPenDownRef.current) {
      const last = penDraftRef.current[penDraftRef.current.length - 1];
      if (!last || last.x !== pt.x || last.y !== pt.y) {
        penDraftRef.current.push(pt);
        redraw();
      }
      return;
    }

    if (g.type === "rect-create" && rectDraft) {
      setRectDraft((d) => (d ? { ...d, x1: pt.x, y1: pt.y } : null));
      return;
    }

    if (g.type === "move") {
      const dx = pt.x - g.start.x;
      const dy = pt.y - g.start.y;
      setAnnotations((prev) =>
        prev.map((a) => {
          if (a.id !== g.id) return a;
          if (a.tool === "rectangle" && g.snapshot.tool === "rectangle") {
            return {
              ...a,
              x: g.snapshot.x + dx,
              y: g.snapshot.y + dy,
            };
          }
          if (a.tool === "text" && g.snapshot.tool === "text") {
            return {
              ...a,
              x: g.snapshot.x + dx,
              y: g.snapshot.y + dy,
            };
          }
          return a;
        })
      );
      return;
    }

    if (g.type === "resize" && g.initial.tool === "rectangle") {
      const next = resizeRectangle(g.initial, g.handle, pt.x, pt.y, MIN_RECT_SIZE);
      setAnnotations((prev) => prev.map((a) => (a.id === g.id && a.tool === "rectangle" ? next : a)));
    }
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    try {
      (e.target as HTMLCanvasElement).releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    const g = gestureRef.current;
    if (g.type === "pen") {
      endPen();
      return;
    }
    if (g.type === "rect-create") {
      commitRectDraft();
      return;
    }
    if (g.type === "move" || g.type === "resize") {
      gestureRef.current = { type: "none" };
    }
  };

  const handleUndo = () => {
    setAnnotations((prev) => (prev.length ? prev.slice(0, -1) : prev));
    setSelectedId(null);
    setEditingTextId(null);
  };

  const handleClear = () => {
    penDraftRef.current = [];
    isPenDownRef.current = false;
    gestureRef.current = { type: "none" };
    setRectDraft(null);
    setAnnotations([]);
    setSelectedId(null);
    setEditingTextId(null);
  };

  useImperativeHandle(
    ref,
    () => ({
      hasAnnotations: () => annotationsRef.current.length > 0,
      getAnnotationPayload: (): TeacherCorrectionPayload | null => {
        if (annotationsRef.current.length === 0 || exportSize.w <= 0) return null;
        return {
          version: 2,
          canvasWidth: exportSize.w,
          canvasHeight: exportSize.h,
          annotations: annotationsRef.current,
        };
      },
      exportMergedImage: async (filename: string): Promise<File> => {
        const img = displayImgRef.current;
        if (!img || !img.complete || img.naturalWidth <= 0 || exportSize.w <= 0 || exportSize.h <= 0) {
          throw new Error("Image not ready for export.");
        }
        const out = document.createElement("canvas");
        out.width = exportSize.w;
        out.height = exportSize.h;
        const ctx = out.getContext("2d");
        if (!ctx) throw new Error("Could not create export context.");
        try {
          ctx.drawImage(img, 0, 0, exportSize.w, exportSize.h);
          drawAnnotations(ctx, annotationsRef.current, exportSize.w, null);
        } catch (err) {
          throw new Error(
            err instanceof Error ? err.message : "Could not merge image (CORS or decode error)."
          );
        }
        return new Promise((resolve, reject) => {
          out.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error("Could not create image file."));
                return;
              }
              resolve(new File([blob], filename, { type: "image/png" }));
            },
            "image/png",
            1
          );
        });
      },
    }),
    [exportSize.w, exportSize.h]
  );

  const editingText = editingTextId
    ? (annotations.find((a) => a.id === editingTextId && a.tool === "text") as TextAnnotation | undefined)
    : undefined;

  const onTextChange = (v: string) => {
    if (!editingTextId) return;
    setAnnotations((prev) =>
      prev.map((a) => (a.id === editingTextId && a.tool === "text" ? { ...a, text: v } : a))
    );
  };

  const canvasCursor =
    activeTool === "pen" || activeTool === "rectangle" || activeTool === "text"
      ? "crosshair"
      : "default";

  if (loadStatus === "error") {
    return (
      <div className="rounded-lg bg-slate-100 dark:bg-slate-900/60 p-4 text-center text-sm text-slate-600 dark:text-slate-400 min-h-[12rem] flex flex-col items-center justify-center">
        <p>Image could not be loaded.</p>
        <a
          href={imageUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-2 text-primary-600 dark:text-primary-400 underline"
        >
          Open image in new tab
        </a>
      </div>
    );
  }

  const ready = loadStatus === "ready";
  const zoomAtMin = userZoom <= USER_ZOOM_MIN + 0.001;
  const zoomAtMax = userZoom >= USER_ZOOM_MAX - 0.001;
  const zoomNearDefault = Math.abs(userZoom - 1) < 0.001;

  return (
    <div className="space-y-2">
      <TeacherCorrectionToolbar
        activeTool={activeTool}
        onToolChange={(t) => {
          setActiveTool(t);
          if (t !== "select") setSelectedId(null);
        }}
        onUndo={handleUndo}
        onClear={handleClear}
        canUndo={annotations.length > 0}
        hasMarks={annotations.length > 0}
        disabled={!ready}
        userZoom={userZoom}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onResetZoom={handleResetZoom}
        zoomAtMin={zoomAtMin}
        zoomAtMax={zoomAtMax}
        zoomIsDefault={zoomNearDefault}
      />
      <div
        ref={zoomWheelRef}
        className="relative rounded-lg bg-slate-100 dark:bg-slate-900/60 flex justify-center items-start min-h-[12rem] max-h-[min(70vh,32rem)] overflow-auto"
      >
        {!ready && (
          <p className="absolute inset-0 flex items-center justify-center text-sm text-slate-500 dark:text-slate-400 z-10 bg-slate-100/80 dark:bg-slate-900/80 pointer-events-none">
            Loading image…
          </p>
        )}
        <div
          className="relative inline-flex max-w-full flex-col items-center p-2"
          style={{
            transform: `scale(${userZoom})`,
            transformOrigin: "center top",
          }}
        >
          <div className="relative inline-block max-w-full">
            <img
              ref={displayImgRef}
              src={imageUrl}
              alt="Student submission"
              crossOrigin="anonymous"
              onLoad={handleImgLoad}
              onError={handleImgError}
              className={`block max-w-full w-auto max-h-[min(60vh,28rem)] h-auto mx-auto object-contain select-none pointer-events-none ${
                ready ? "opacity-100" : "opacity-0"
              }`}
              draggable={false}
            />
            {ready && (
              <>
                <canvas
                  ref={canvasRef}
                  className="absolute left-0 top-0 w-full h-full touch-none"
                  style={{ cursor: canvasCursor }}
                  onPointerDown={handlePointerDown}
                  onPointerMove={handlePointerMove}
                  onPointerUp={handlePointerUp}
                  onPointerLeave={() => {
                    if (gestureRef.current.type === "pen" && isPenDownRef.current) endPen();
                    if (gestureRef.current.type === "rect-create") commitRectDraft();
                    if (gestureRef.current.type === "move" || gestureRef.current.type === "resize") {
                      gestureRef.current = { type: "none" };
                    }
                  }}
                  onPointerCancel={handlePointerUp}
                />
                {editingText && (
                  <textarea
                    ref={textareaRef}
                    value={editingText.text}
                    onChange={(ev) => onTextChange(ev.target.value)}
                    onBlur={endTextEditing}
                    onKeyDown={(ev) => {
                      if (ev.key === "Escape") {
                        ev.preventDefault();
                        textareaRef.current?.blur();
                      }
                    }}
                    className="absolute z-20 rounded border border-primary-500 bg-white/95 dark:bg-slate-900/95 text-slate-900 dark:text-slate-100 p-1 shadow-md resize-none outline-none min-w-[6rem] min-h-[2rem]"
                    style={{
                      left: editingText.x * displayScale,
                      top: editingText.y * displayScale,
                      width: Math.max(96, estimateTextBounds(editingText).w * displayScale),
                      fontSize: editingText.fontSize * displayScale,
                      lineHeight: 1.35,
                    }}
                    aria-label="Correction text"
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

TeacherImageCorrectionEditor.displayName = "TeacherImageCorrectionEditor";
